import React, { useMemo } from 'react';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import { SplineChartV2 } from 'src/components/BeaconV2Components/SplineChartV2';
import { getAppName } from 'src/utils/app';
import { INDEX_FIELDS } from 'src/utils/entityDefinitions';
import {
  FORECAST_PERIOD_TO_LABEL_MAP,
  FORECAST_COMPARISON_TO_LABEL_MAP
} from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/dropdowns';
import {
  useForecastComparisonPeriod,
  useForecastPeriod,
  useForecastType,
  usePreviousForecastWeekId
} from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/hooks';
import { ForecastType, ForecastComparisonPeriod } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/types';
import SplineChartLoading from 'src/components/BeaconV2Components/SplineChartLoading/SplineChartLoading';
import { getDirection } from 'src/components/BeaconV2Components/utils/chartStyles';
import useForecastMetricByWeekIdV2, {
  UseForecastMetricByWeekIdV2Result
} from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useForecastMetricByWeekIdV2';
import useForecastDerivedMetricByWeekId from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useForecastDerivedMetricByWeekId';
import { getCardTitle } from 'src/components/BeaconV2Components/KeyMetricCards/SmartKeyMetricCard';
import saveAs from 'file-saver';
import { formatNumericDateToMonthDayYear, getStartAndEndOfWeek } from 'src/utils/dateUtils';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { weekIdToTimestamp } from 'src/components/BeaconV2Components/SplineChartV2';

interface ForecastsLineChartV2Props {
  indexName: string;
  fieldName: string;
  subtitle?: string;
  titleOverrides?: Record<string, Record<string, string>>;
}

/**
 * ForecastsLineChartV2 - Uses SplineChartV2 for forecast visualization
 */
function ForecastsLineChartV2({
  subtitle,
  indexName,
  fieldName,
  titleOverrides,
  primarySeries,
  comparisonSeries,
  forecastSeries,
  primaryData,
  comparisonData,
  forecastData,
  formattedPrimaryTotal,
  formattedComparisonTotal,
  formattedPercentChange,
  percentChange,
  isLoading
}: ForecastsLineChartV2Props & UseForecastMetricByWeekIdV2Result) {
  const forecastPeriod = useForecastPeriod();
  const forecastType = useForecastType();
  const forecastComparisonPeriod = useForecastComparisonPeriod();
  const field = useMemo(() => INDEX_FIELDS.getField(getAppName(), indexName, fieldName), [indexName, fieldName]);
  const cardTitle = getCardTitle(indexName, fieldName, titleOverrides);
  const previousForecastWeekId = usePreviousForecastWeekId();
  console.log('primaryData', primaryData);
  console.log('comparisonData', comparisonData);
  console.log('forecastData', forecastData);
  console.log("forecast period", forecastPeriod);

  const convertSeriesToDelimitedData = () => {
    try {
      if (forecastData.length > 1) {
        const forecastCSVFormatter = () => {
          // Forecast data has default 1 week of actual data, so we need to remove it
          const trailedForecastedData = primaryData.length > 0 ? forecastData.slice(1) : forecastData;
          const combinedMainData = [...primaryData, ...trailedForecastedData];

          const comparisonObj: Record<number, number | null> = {};
          comparisonData.forEach((point) => {
            const actualWeekId = previousForecastWeekId(point.weekId);
            comparisonObj[actualWeekId] = point.value;
          });

          const result: (string | number | null)[][] = [];
          for (let i = 0; i < combinedMainData.length; i++) {
            const mainPoint = combinedMainData[i];
            const mainWeekId = mainPoint.weekId;
            const formattedMainEndDate = formatNumericDateToMonthDayYear(
              getStartAndEndOfWeek(mainWeekId, true).endDate
            );
            const comparisonWeekId = previousForecastWeekId(mainWeekId);
            const formattedComparisonEndDate =
              formatNumericDateToMonthDayYear(getStartAndEndOfWeek(comparisonWeekId, true).endDate) || comparisonWeekId;
            const primaryDataType = i < primaryData.length ? 'Actual' : 'Forecasted';
            result.push([
              // Comparison Week End Date
              `"${formattedComparisonEndDate}"`,
              // Comparison Week End Id
              comparisonWeekId,
              // Comparison Data Type -- this is always Actual since historic data
              'Actual',
              // Main Week End Date
              `"${formattedMainEndDate}"`,
              // Main Week End Id
              mainWeekId,
              // Main Data Type - Actual vs Forecasted
              primaryDataType,
              // Comparison Value
              comparisonObj[comparisonWeekId] ?? '',
              // Main Value
              mainPoint.value
            ]);
          }
          const defaultHeader = [
            'Comparison Week End Date',
            'Comparison Week End Id',
            'Comparison Data Type',
            'Main Week End Date',
            'Main Week End Id',
            'Main Data Type',
            `Comparison - ${cardTitle}`,
            `Main - ${cardTitle}`
          ];
          return [defaultHeader, ...result];
        };
        const resultData = forecastCSVFormatter();
        let csvContent = '';

        resultData.forEach((row) => {
          csvContent += row.join(',') + '\n';
        });
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
        saveAs(blob, `${cardTitle} Projections`);
      }
    } catch (e) {
      console.error('error with exporting forecast line chart', e);
    }
    return Promise.resolve();
  };

  return (
    <BeaconChartWithLegend
      subtitle={
        <BeaconSubtitleContainer>
          <Text variant="body1">{subtitle}</Text>
        </BeaconSubtitleContainer>
      }
      title={`${cardTitle} Projections`}
      primaryLegendProps={{
        displayName: `${FORECAST_PERIOD_TO_LABEL_MAP[forecastPeriod]} (${
          forecastType === ForecastType.ADJUSTED ? 'Adjusted' : 'Unadjusted'
        })`,
        metric: formattedPrimaryTotal,
        direction: getDirection(percentChange),
        change: formattedPercentChange
      }}
      comparisonLegendProps={{
        displayName: `${FORECAST_COMPARISON_TO_LABEL_MAP[forecastComparisonPeriod]} (Actuals)`,
        metric: formattedComparisonTotal
      }}
      loading={isLoading}
      loadingComponent={<SplineChartLoading />}
      convertSeriesToDelimitedData={convertSeriesToDelimitedData}
    >
      <SplineChartV2
        primarySeries={primarySeries}
        comparisonSeries={comparisonSeries}
        forecastSeries={forecastSeries}
        metricType={field.metricType}
        isPriorPeriodOverride={forecastComparisonPeriod === ForecastComparisonPeriod.PRIOR_PERIOD}
      />
    </BeaconChartWithLegend>
  );
}

/**
 * Derived forecast line chart for metrics like purchase rate
 */
function DerivedForecastLineChartV2({ indexName, fieldName, subtitle, titleOverrides }: ForecastsLineChartV2Props) {
  // For derived metrics, we still use the V1 hook but need to convert data
  // TODO: Create a V2 version of useForecastDerivedMetricByWeekId
  const args = useForecastDerivedMetricByWeekId({ indexName, fieldName });

  // Convert V1 data to V2 format for now
  const primarySeries = useMemo(() => ({
    id: `${indexName}-${fieldName}-primary`,
    name: 'Primary',
    data: args.primaryData.map(([timestamp, value]: [number, number]) => ({
      weekId: Math.floor(timestamp / 1000), // This is a placeholder - need proper conversion
      value
    }))
  }), [indexName, fieldName, args.primaryData]);

  const comparisonSeries = useMemo(() => ({
    id: `${indexName}-${fieldName}-comparison`,
    name: 'Comparison',
    data: args.secondaryData.map(([timestamp, value]: [number, number]) => ({
      weekId: Math.floor(timestamp / 1000),
      value
    }))
  }), [indexName, fieldName, args.secondaryData]);

  const forecastSeries = useMemo(() => ({
    id: `${indexName}-${fieldName}-forecast`,
    name: 'Forecast',
    data: (args.forecastData || []).map(([timestamp, value]: [number, number]) => ({
      weekId: Math.floor(timestamp / 1000),
      value
    })),
    isDashed: true
  }), [indexName, fieldName, args.forecastData]);

  return (
    <ForecastsLineChartV2
      subtitle={subtitle}
      indexName={indexName}
      fieldName={fieldName}
      titleOverrides={titleOverrides}
      primarySeries={primarySeries}
      comparisonSeries={comparisonSeries}
      forecastSeries={forecastSeries}
      primaryData={primarySeries.data}
      comparisonData={comparisonSeries.data}
      forecastData={forecastSeries.data}
      formattedPrimaryTotal={args.formattedPrimaryTotal}
      formattedComparisonTotal={args.formattedSecondaryTotal}
      formattedPercentChange={args.formattedPercentChange}
      percentChange={args.percentChange}
      isLoading={args.isLoading}
    />
  );
}

/**
 * Default forecast line chart for standard metrics
 */
function DefaultForecastLineChartV2({ indexName, fieldName, subtitle, titleOverrides }: ForecastsLineChartV2Props) {
  const args = useForecastMetricByWeekIdV2({
    fieldName,
    indexName
  });

  return (
    <ForecastsLineChartV2
      subtitle={subtitle}
      indexName={indexName}
      fieldName={fieldName}
      titleOverrides={titleOverrides}
      {...args}
    />
  );
}

/**
 * Smart component that chooses between derived and default forecast chart
 */
const SmartForecastsLineChartV2 = ({ indexName, fieldName, subtitle, titleOverrides }: ForecastsLineChartV2Props) => {
  const field = useMemo(() => INDEX_FIELDS.getField(getAppName(), indexName, fieldName), [indexName, fieldName]);

  return field.aggregationFunctionType === 'derived' ? (
    <DerivedForecastLineChartV2
      indexName={indexName}
      fieldName={fieldName}
      subtitle={subtitle}
      titleOverrides={titleOverrides}
    />
  ) : (
    <DefaultForecastLineChartV2
      indexName={indexName}
      fieldName={fieldName}
      subtitle={subtitle}
      titleOverrides={titleOverrides}
    />
  );
};

export default SmartForecastsLineChartV2;
